import { FaceFlowError, safeFaceFlowError } from './faceAttendanceErrors';

const CLIENT_KIOSK_MESSAGES: Record<string, string> = {
  CAPTURE_CANCELED: 'Capture canceled. Tap Mark Attendance to try again.',
  CAPTURE_FAILED: 'Could not capture a clear photo. Please try again.',
  SERVICE_UNAVAILABLE: 'Service temporarily unavailable. Please try again shortly.',
};

const GENERIC_MESSAGES = new Set([
  CLIENT_KIOSK_MESSAGES.SERVICE_UNAVAILABLE,
  'Face attendance is temporarily unavailable.',
  'Something went wrong. Please try again.',
  'Something went wrong on the server. Please try again later.',
]);

export function kioskFriendlyMessage(code: string, fallback?: string): string {
  const trimmed = fallback?.trim();
  if (trimmed && !GENERIC_MESSAGES.has(trimmed)) return trimmed;
  return CLIENT_KIOSK_MESSAGES[code] ?? CLIENT_KIOSK_MESSAGES.SERVICE_UNAVAILABLE;
}

export function toKioskFaceError(error: unknown): FaceFlowError {
  if (error instanceof FaceFlowError) {
    return new FaceFlowError(
      error.code,
      kioskFriendlyMessage(error.code, error.message),
    );
  }

  const flowError = safeFaceFlowError(error);
  const status =
    typeof error === 'object' &&
    error &&
    'response' in error &&
    typeof (error as { response?: { status?: unknown } }).response?.status ===
      'number'
      ? (error as { response: { status: number } }).response.status
      : typeof error === 'object' &&
          error &&
          'statusCode' in error &&
          typeof (error as { statusCode?: unknown }).statusCode === 'number'
        ? (error as { statusCode: number }).statusCode
        : null;

  const transportCode =
    typeof error === 'object' &&
    error &&
    'code' in error &&
    typeof (error as { code?: unknown }).code === 'string'
      ? (error as { code: string }).code
      : '';

  let code = flowError.code;
  const hasApiMessage =
    Boolean(flowError.message?.trim()) &&
    !GENERIC_MESSAGES.has(flowError.message.trim());
  if (
    !hasApiMessage &&
    (status === 502 ||
      status === 503 ||
      status === 504 ||
      transportCode === 'ECONNABORTED' ||
      transportCode === 'ERR_NETWORK' ||
      code === 'ECONNABORTED' ||
      code === 'ERR_NETWORK' ||
      code === 'NETWORK_ERROR' ||
      code === 'REQUEST_TIMEOUT')
  ) {
    code = 'SERVICE_UNAVAILABLE';
  }

  return new FaceFlowError(code, kioskFriendlyMessage(code, flowError.message));
}

export function kioskAdminErrorMessage(
  error: unknown,
  fallback = 'Unable to complete kiosk action.',
): string {
  if (
    typeof error === 'object' &&
    error &&
    'message' in error &&
    typeof (error as { message?: unknown }).message === 'string'
  ) {
    const message = (error as { message: string }).message.trim();
    if (message && !GENERIC_MESSAGES.has(message)) return message;
  }
  if (
    typeof error === 'object' &&
    error &&
    'code' in error &&
    typeof (error as { code?: unknown }).code === 'string'
  ) {
    return kioskFriendlyMessage((error as { code: string }).code);
  }
  return fallback;
}
